import * as pdfjsLib from 'pdfjs-dist';
import React, { useEffect, useRef, useState } from 'react';
import './CustomPDFViewer.scss';
import PasswordGate from './PasswordGate';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

interface CustomPDFViewerProps {
  source: string | Uint8Array;
  onPageChange?: (pageNumber: number, totalPages: number) => void;
  onDocumentLoad?: (totalPages: number) => void;
  onError?: (error: Error) => void;
  initialPage?: number;
  initialScale?: number;
  className?: string;
  style?: React.CSSProperties;
  onFullscreenChange?: (isFullscreen: boolean) => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
}

const CustomPDFViewer: React.FC<CustomPDFViewerProps> = ({
  source,
  onPageChange,
  onDocumentLoad,
  onError,
  initialPage = 1,
  initialScale = 1,
  className = '',
  style = {},
  onFullscreenChange,
  isFullscreen: externalIsFullscreen,
  onToggleFullscreen,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const [pdfDocument, setPdfDocument] =
    useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPageNumber, setCurrentPageNumber] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(initialScale);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [internalIsFullscreen, setInternalIsFullscreen] = useState(false);
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);
  const passwordCallbackRef = useRef<(p: string) => void>(() => {});

  // Use external fullscreen state if provided, otherwise use internal state
  const isFullscreen =
    externalIsFullscreen !== undefined
      ? externalIsFullscreen
      : internalIsFullscreen;
  const setIsFullscreen =
    externalIsFullscreen !== undefined
      ? () => onToggleFullscreen?.()
      : setInternalIsFullscreen;

  // Animation frame for smooth zooming at 60 FPS
  const animationFrameRef = useRef<number | null>(null);
  const lastZoomUpdateRef = useRef<number>(0);
  const ZOOM_THROTTLE_MS = 16;

  const loadDocument = async (docSource: string) => {
    try {
      setIsLoading(true);
      setError(null);

      if (
        !docSource ||
        (typeof docSource === 'string' && docSource.trim() === '')
      ) {
        throw new Error('No PDF source provided');
      }

      const loadingTask = pdfjsLib.getDocument(docSource);

      loadingTask.onPassword = (callback: () => void, reason: number) => {
        passwordCallbackRef.current = callback;
        setIsPasswordProtected(true);
        setIsLoading(false);
        if ([pdfjsLib.PasswordResponses.INCORRECT_PASSWORD].includes(reason)) {
          setError('Incorrect password');
        }
      };

      const pdf = await loadingTask.promise;

      setPdfDocument(pdf);
      setTotalPages(pdf.numPages);
      onDocumentLoad?.(pdf.numPages);
      setIsPasswordProtected(false);
    } catch (err) {
      console.error('PDF loading error:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load PDF';
      setError(errorMessage);
      onError?.(err instanceof Error ? err : new Error(errorMessage));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDocument(source as string);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  // Mouse wheel zoom handler (only when Cmd/Ctrl is pressed)
  const handleWheel = (e: WheelEvent) => {
    // Only handle wheel events when Command (Mac) or Control (Windows) is pressed
    if (!e.metaKey && !e.ctrlKey) {
      return; // Allow normal scrolling if modifier key is not pressed
    }

    e.preventDefault();

    const now = performance.now();
    if (now - lastZoomUpdateRef.current < ZOOM_THROTTLE_MS) {
      return; // Throttle to 60 FPS
    }

    const delta = e.deltaY;
    const zoomFactor = 0.1; // How much to zoom per wheel step

    if (delta < 0) {
      // Zoom in (wheel up)
      setScale((prev) => Math.min(prev * (1 + zoomFactor), 2.0));
    } else {
      // Zoom out (wheel down)
      setScale((prev) => Math.max(prev * (1 - zoomFactor), 0.25));
    }

    lastZoomUpdateRef.current = now;
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const renderPage = async () => {
      const container = containerRef.current;
      if (!pdfDocument || !pdfContentRef.current || !container) return;

      let finalScale = scale;
      let baseWidth = 0;
      const page = await pdfDocument.getPage(currentPageNumber);
      // find scale to fit page in container
      if (scale === 1.0) {
        const containerWidth = container.offsetWidth || 0;
        const baseViewport = page.getViewport({ scale: 1 });
        baseWidth = baseViewport.width;
        let calculatedScale = (containerWidth * 0.9) / baseWidth;

        finalScale = Math.min(Math.max(calculatedScale, 0.1), 2.0);
        setScale(finalScale);
      } else {
        finalScale = scale;
      }

      try {
        const viewport = page.getViewport({ scale: finalScale });
        pdfContentRef.current.innerHTML = '';

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) return;

        const devicePixelRatio = window.devicePixelRatio || 1;
        const scaledViewport = page.getViewport({
          scale: finalScale * devicePixelRatio,
        });
        canvas.height = scaledViewport.height;
        canvas.width = scaledViewport.width;
        canvas.style.height = `${viewport.height}px`;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.display = 'block';

        const renderContext = {
          canvasContext: context,
          viewport: scaledViewport,
        };

        await page.render(renderContext).promise;

        const pdfContentWrapper = document.createElement('div');
        pdfContentWrapper.style.position = 'relative';
        pdfContentWrapper.style.display = 'inline-block';
        pdfContentWrapper.appendChild(canvas);

        // Render annotation layer for links with corrected coordinates
        const annotations = await page.getAnnotations();
        console.log('Total annotations found:', annotations.length);
        console.log('All annotations:', annotations);

        if (annotations.length > 0) {
          const annotationLayer = document.createElement('div');
          annotationLayer.style.position = 'absolute';
          annotationLayer.style.top = '0';
          annotationLayer.style.left = '0';
          annotationLayer.style.width = '100%';
          annotationLayer.style.height = '100%';
          annotationLayer.style.pointerEvents = 'none';

          annotations.forEach((annotation: any) => {
            if (annotation.subtype === 'Link' && annotation.url) {
              console.log('Found external link annotation:', annotation);

              const linkDiv = document.createElement('div');
              linkDiv.style.position = 'absolute';
              // Scale coordinates from PDF points to pixels
              // PDF Y-axis is inverted (0 at bottom), so we need to flip it
              linkDiv.style.left = `${annotation.rect[0] * scale}px`;
              linkDiv.style.top = `${
                viewport.height - annotation.rect[3] * scale
              }px`;
              linkDiv.style.width = `${
                (annotation.rect[2] - annotation.rect[0]) * scale
              }px`;
              linkDiv.style.height = `${
                (annotation.rect[3] - annotation.rect[1]) * scale
              }px`;
              linkDiv.style.pointerEvents = 'auto';
              linkDiv.style.cursor = 'pointer';
              linkDiv.style.backgroundColor = 'rgba(255, 0, 0, 0.1)'; // Debug: make visible

              const link = document.createElement('a');
              link.href = annotation.url;
              link.target = '_blank';
              link.rel = 'noopener noreferrer';
              link.style.display = 'block';
              link.style.width = '100%';
              link.style.height = '100%';
              link.onclick = (e) => {
                e.preventDefault();
                window.open(annotation.url, '_blank', 'noopener,noreferrer');
              };
              console.log('External link:', annotation.url);

              linkDiv.appendChild(link);
              annotationLayer.appendChild(linkDiv);
            }
          });

          pdfContentWrapper.appendChild(annotationLayer);
        }

        pdfContentRef.current.innerHTML = '';
        pdfContentRef.current.appendChild(pdfContentWrapper);

        onPageChange?.(currentPageNumber, totalPages);
      } catch (err) {
        console.error('Error rendering page:', err);
        setError('Failed to render page');
      }
    };

    renderPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDocument, currentPageNumber, scale, totalPages]);

  const goToPreviousPage = () => {
    if (currentPageNumber > 1) {
      setCurrentPageNumber(currentPageNumber - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPageNumber < totalPages) {
      setCurrentPageNumber(currentPageNumber + 1);
    }
  };

  const goToPage = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPageNumber(pageNumber);
    }
  };

  const zoomIn = () => {
    setScale((prev) => Math.max(prev, Math.min(prev * 1.2, 2.0)));
  };

  const zoomOut = () => {
    setScale((prev) => Math.min(prev, Math.max(prev / 1.2, 0.1)));
  };

  const toggleFullscreen = () => {
    // Use CSS-based fullscreen only
    const newFullscreenState = !isFullscreen;
    setIsFullscreen(newFullscreenState);
    onFullscreenChange?.(newFullscreenState);
  };

  // Handle keyboard navigation and fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Handle ESC key for fullscreen exit
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
        onFullscreenChange?.(false);
        return;
      }

      // Handle arrow keys for page navigation
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPreviousPage();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNextPage();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageNumber, totalPages]);

  if (error && !isPasswordProtected) {
    return (
      <div className={`custom-pdf-viewer error ${className}`} style={style}>
        <div className="error-message">
          <h3>Error loading PDF</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`custom-pdf-viewer ${
        isFullscreen ? 'fullscreen' : ''
      } ${className}`}
      style={style}>
      <div className="pdf-toolbar">
        <div className="toolbar-left">
          <button
            onClick={goToPreviousPage}
            disabled={currentPageNumber <= 1}
            title="Previous page">
            ←
          </button>

          <span
            className={
              isPasswordProtected ? 'blur-area page-info' : 'page-info'
            }>
            <input
              type="number"
              min={1}
              max={totalPages}
              value={currentPageNumber}
              onChange={(e) => goToPage(parseInt(e.target.value) || 1)}
              disabled={isPasswordProtected}
            />
            <span>of {totalPages}</span>
          </span>

          <button
            onClick={goToNextPage}
            disabled={currentPageNumber >= totalPages || isPasswordProtected}
            title="Next page">
            →
          </button>
          <button
            onClick={zoomOut}
            title="Zoom out"
            disabled={isPasswordProtected}>
            -
          </button>

          <button
            onClick={zoomIn}
            title="Zoom in"
            disabled={isPasswordProtected}>
            +
          </button>

          <button onClick={toggleFullscreen}>x</button>
        </div>
      </div>

      <div className="pdf-container">
        {isLoading && <div className="loading-spinner">Loading PDF...</div>}

        {isPasswordProtected ? (
          <PasswordGate
            onSubmit={(password) => {
              passwordCallbackRef.current?.(password);
            }}
            error={error || ''}
            setError={setError}
          />
        ) : (
          <div ref={pdfContentRef} className="pdf-content" />
        )}
      </div>
    </div>
  );
};

export default React.memo(CustomPDFViewer);
